'use client';

/**
 * CardShell — the ONE visual container every card sits in.
 *
 * The visual twin of CardChromeProvider: chrome-provider gives a card its
 * title/icon/typography as data; CardShell gives it its BOX. It owns the
 * container chrome that used to be hand-rolled per card — `bg-card`, `border`,
 * `rounded`, and elevation (`--elev-1`) — so those become a property of the
 * shell, not 20 copy-pasted class strings. Change the box once, every card
 * changes; add depth once, every card floats.
 *
 * Cards render their CONTENT into the shell; they no longer own the box.
 * Layout/behaviour classes (flex, h-full, overflow, group) still ride in
 * `className` — the shell owns the housing, the card owns its insides.
 *
 * Escape hatch: `bare` renders children with no box, for the genuine
 * exceptions that supply their own housing (a band-tinted section, a chip).
 * That's the 10% the doctrine always allowed — as a declared prop, not a
 * re-implemented box.
 *
 * @layer presentation
 * @arch_component_type design-system
 */

import { createElement } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

type Elevation = 0 | 1 | 2;

const ELEVATION_CLASS: Record<Elevation, string> = {
  0: '',
  1: 'shadow-elev-1',
  2: 'shadow-elev-2',
};

interface CardShellProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** Resting elevation. Default 1 (a floating card). 0 = flush, for nested
   *  surfaces that shouldn't cast their own shadow. */
  elevation?: Elevation;
  /** Lift + border-accent on hover, for clickable cards. */
  interactive?: boolean;
  /** Render children with no box — the card supplies its own housing. */
  bare?: boolean;
  /** Element tag (semantic): 'div' (default) | 'article' | 'section' | 'button'.
   *  'button' makes a clickable tile a real, keyboard-accessible button in the
   *  standard container (pass `interactive` + `onClick`). */
  as?: 'div' | 'article' | 'section' | 'button';
}

export function CardShell({
  children,
  className,
  elevation = 1,
  interactive = false,
  bare = false,
  as = 'div',
  ...rest
}: CardShellProps) {
  const boxClass = bare
    ? className
    : cn(
        // The one default box — the whole point of the shell.
        'bg-card border border-border rounded-lg',
        ELEVATION_CLASS[elevation],
        interactive &&
          'cursor-pointer transition-all hover:border-primary/50 hover:shadow-elev-2',
        className,
      );
  return createElement(as, { className: boxClass, ...rest }, children);
}
